import React from "react";
import { Typography, Box, Theme } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Appointment } from "@/domain/appointment.interface";
import { Chip } from "@/components/chips/Chip";

dayjs.extend(utc);

interface AppointmentItemProps {
  appointment: Appointment;
  theme: Theme;
}

const getStatusBarColor = (uiStatus: string, theme: Theme): string => {
  switch (uiStatus) {
    case "MISSED":
      return theme.palette.textMain.light2 || "#99979A";
    case "SCHEDULED_MISSED":
      return theme.palette.error.main;
    case "SCHEDULED_BUT_FAILED":
      return theme.palette.warning.main; // Orange
    case "CHECKED_IN":
      return theme.palette.textMain.light2 || "#99979A"; // Gray
    case "CANCELLED":
      return theme.palette.textMain.light2 || "#99979A"; // Gray
    default:
      return theme.palette.brand.main; // Green for scheduled
  }
};

const getStatusColor = (uiStatus: string, theme: Theme): string => {
  switch (uiStatus) {
    case "MISSED":
      return theme.palette.textMain.light2 || "#99979A";
    case "SCHEDULED_MISSED":
      return theme.palette.error.main;
    case "SCHEDULED_BUT_FAILED":
      return theme.palette.warning.main; // Orange
    case "CHECKED_IN":
      return theme.palette.menu.dark || "#1A43D2"; // Blue
    case "CANCELLED":
      return theme.palette.textMain.light2 || "#99979A"; // Gray
    default:
      return theme.palette.menu.dark || "#1A43D2"; // Green for scheduled
  }
};

const getStatusChipVariant = (
  uiStatus: string
): "RED" | "GREEN" | "YELLOW" | "ORANGE" | "GRAY" => {
  switch (uiStatus) {
    case "MISSED":
    case "SCHEDULED_MISSED":
      return "RED";
    case "SCHEDULED_BUT_FAILED":
      return "ORANGE";
    case "CHECKED_IN":
      return "GREEN";
    case "CANCELLED":
      return "GRAY";
    default:
      return "GREEN"; // For scheduled appointments
  }
};

const getStatusLabel = (uiStatus: string): string => {
  switch (uiStatus) {
    case "x":
    case "SCHEDULED_MISSED":
      return "Missed";
    case "SCHEDULED_BUT_FAILED":
      return "Validation Failed";
    case "CHECKED_IN":
      return "Checked In";
    case "CANCELLED":
      return "Cancelled";
    default:
      return ""; // No chip for scheduled appointments by default
  }
};

const AppointmentItem: React.FC<AppointmentItemProps> = ({
  appointment,
  theme,
}) => {
  const statusColor = getStatusColor(appointment.uiStatus, theme);
  const statusBarColor = getStatusBarColor(appointment.uiStatus, theme);
  const startUtc = dayjs.utc(appointment.startDate);
  const endUtc = dayjs.utc(appointment.endDate);

  return (
    <Box
      sx={{
        display: "flex",
        mb: 2.5,
        gap: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          backgroundColor: `${statusBarColor}`,
          borderRadius: 1,
          width: "8px",
        }}
      ></Box>
      <Box sx={{ flexGrow: 1 }}>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: statusColor,
              fontWeight: 600,
            }}
          >
            Appointment
            {appointment.uiStatus === "SCHEDULED_MISSED" && " (no missed reason)"}
          </Typography>
          {getStatusLabel(appointment.uiStatus) && (
            <Chip
              variant={getStatusChipVariant(appointment.uiStatus)}
              theme={theme}
            >
              {getStatusLabel(appointment.uiStatus)}
            </Chip>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "left",
          }}
        >
          <Typography
            variant="labelLarge"
            color={theme.palette.icon.dark}
            sx={{ fontWeight: 600 }}
          >
            {appointment.subject}
          </Typography>

          <Typography variant="body2" color={theme.palette.icon.dark}>
            {startUtc.format("DD MMM YYYY")} • {startUtc.format("hh:mm A")} -{" "}
            {endUtc.format("hh:mm A")}
          </Typography>

          {appointment.treatmentAgency && (
            <>
              <Typography
                variant="bodyMedium"
                color={theme.palette.icon.dark}
                sx={{
                  fontWeight: 600,
                }}
              >
                {appointment.treatmentAgency.name}
              </Typography>
              <Typography variant="bodyMedium" color={theme.palette.icon.dark}>
                Location:{" "}
                {appointment.treatmentAgency?.location?.addressLine1 || ""}
              </Typography>
              <Typography variant="bodyMedium" color={theme.palette.icon.dark}>
                Email: {appointment.treatmentAgency?.location?.email || ""}
              </Typography>
              <Typography variant="bodyMedium" color={theme.palette.icon.dark}>
                Phone: {appointment.treatmentAgency?.location?.phone || ""}
              </Typography>
            </>
          )}
          {appointment.contactName && <Typography variant="bodyMedium" color={theme.palette.icon.dark}>
            Contact: {appointment.contactName || ""}
          </Typography>}
        </Box>
      </Box>
    </Box>
  );
};

export default AppointmentItem;
